import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { BACKEND_URL } from '../../config/api';
import { toast } from 'sonner';
import {
  Webhook, Plus, Trash2, Pause, Play, Send, RefreshCw,
  CheckCircle2, XCircle, Clock, AlertTriangle, ChevronDown,
  ChevronRight, Copy, Eye, RotateCcw, Activity, Zap, List
} from 'lucide-react';

const API = BACKEND_URL;

const getAuth = () => ({ Authorization: `Bearer ${localStorage.getItem('lms_token')}`, 'Content-Type': 'application/json' });

const STATUS_STYLES = {
  active:     'bg-emerald-100 text-emerald-700 border border-emerald-200',
  paused:     'bg-amber-100 text-amber-700 border border-amber-200',
  disabled:   'bg-gray-100 text-gray-500 border border-gray-200',
  delivered:  'bg-emerald-100 text-emerald-700 border border-emerald-200',
  failed:     'bg-red-100 text-red-700 border border-red-200',
  retrying:   'bg-amber-100 text-amber-700 border border-amber-200',
  pending:    'bg-blue-100 text-blue-700 border border-blue-200',
  attempting: 'bg-purple-100 text-purple-700 border border-purple-200',
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
    {status === 'delivered' && <CheckCircle2 size={11} />}
    {status === 'failed' && <XCircle size={11} />}
    {(status === 'retrying' || status === 'pending' || status === 'attempting') && <Clock size={11} />}
    {status === 'active' && <Zap size={11} />}
    {status === 'paused' && <Pause size={11} />}
    {status}
  </span>
);

const EVENT_LABELS = {
  'student.login': 'Student Login',
  'progress.updated': 'Progress Updated',
  'student.created': 'Student Created',
};

const EVENT_COLORS = {
  'student.login': 'bg-blue-50 text-blue-700 border border-blue-200',
  'progress.updated': 'bg-violet-50 text-violet-700 border border-violet-200',
  'student.created': 'bg-emerald-50 text-emerald-700 border border-emerald-200',
};

const EventTag = ({ type }) => (
  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${EVENT_COLORS[type] || 'bg-gray-100 text-gray-600'}`}>
    {EVENT_LABELS[type] || type}
  </span>
);

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

// ------------------------------------------------------------------ //
//  Stats bar                                                           //
// ------------------------------------------------------------------ //
const StatsBar = ({ stats }) => {
  if (!stats) return null;
  const cards = [
    { label: 'Active Subscriptions', value: stats.subscriptions?.active ?? 0, icon: Zap, color: 'text-emerald-600' },
    { label: 'Paused', value: stats.subscriptions?.paused ?? 0, icon: Pause, color: 'text-amber-600' },
    { label: 'Total Events Fired', value: stats.events?.total ?? 0, icon: Activity, color: 'text-blue-600' },
    { label: 'Delivered', value: stats.deliveries?.delivered ?? 0, icon: CheckCircle2, color: 'text-emerald-600' },
    { label: 'Failed', value: stats.deliveries?.failed ?? 0, icon: XCircle, color: 'text-red-600' },
    { label: 'Pending / Retrying', value: stats.deliveries?.pending ?? 0, icon: Clock, color: 'text-amber-600' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm">
          <c.icon size={16} className={`mb-1 ${c.color}`} />
          <p className="text-xl font-bold text-gray-800">{c.value}</p>
          <p className="text-xs text-gray-500 leading-tight">{c.label}</p>
        </div>
      ))}
    </div>
  );
};

// ------------------------------------------------------------------ //
//  Subscription Form Modal                                             //
// ------------------------------------------------------------------ //
const ALLOWED_EVENTS = ['student.login', 'progress.updated', 'student.created'];

const SubscriptionModal = ({ sub, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: sub?.name || '',
    url: sub?.url || '',
    events: sub?.events || [],
  });
  const [saving, setSaving] = useState(false);

  const toggle = (ev) => setForm(f => ({
    ...f,
    events: f.events.includes(ev) ? f.events.filter(e => e !== ev) : [...f.events, ev]
  }));

  const save = async () => {
    if (!form.name.trim() || !form.url.trim() || form.events.length === 0) {
      toast.error('Fill in name, URL, and select at least one event');
      return;
    }
    setSaving(true);
    try {
      const method = sub ? 'PUT' : 'POST';
      const url = sub ? `${API}/api/admin/webhooks/subscriptions/${sub.id}` : `${API}/api/admin/webhooks/subscriptions`;
      const res = await fetch(url, { method, headers: getAuth(), body: JSON.stringify(form) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      toast.success(sub ? 'Subscription updated' : 'Subscription created');
      onSave();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="subscription-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">{sub ? 'Edit Subscription' : 'New Subscription'}</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              data-testid="sub-name-input"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Google Sheet – Main Tracker"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Destination URL</label>
            <input
              data-testid="sub-url-input"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              placeholder="https://script.google.com/macros/s/..."
              value={form.url}
              onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subscribe to Events</label>
            <div className="space-y-2">
              {ALLOWED_EVENTS.map(ev => (
                <label key={ev} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-blue-600"
                    checked={form.events.includes(ev)}
                    onChange={() => toggle(ev)}
                    data-testid={`event-checkbox-${ev}`}
                  />
                  <EventTag type={ev} />
                  <span className="text-sm text-gray-600">{EVENT_LABELS[ev]}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2 text-sm font-medium hover:bg-gray-50">Cancel</button>
          <button
            data-testid="save-subscription-btn"
            onClick={save}
            disabled={saving}
            className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------ //
//  Subscriptions Tab                                                   //
// ------------------------------------------------------------------ //
const SubscriptionsTab = ({ refresh }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | sub object
  const [testing, setTesting] = useState(null);
  const [showSecret, setShowSecret] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/webhooks/subscriptions`, { headers: getAuth() });
      setSubs(await r.json());
    } catch { toast.error('Failed to load subscriptions'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load, refresh]);

  const toggle = async (sub) => {
    const action = sub.status === 'active' ? 'pause' : 'resume';
    try {
      await fetch(`${API}/api/admin/webhooks/subscriptions/${sub.id}/${action}`, { method: 'PATCH', headers: getAuth() });
      toast.success(`Subscription ${action}d`);
      load();
    } catch { toast.error('Failed'); }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Delete "${sub.name}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API}/api/admin/webhooks/subscriptions/${sub.id}`, { method: 'DELETE', headers: getAuth() });
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const test = async (sub) => {
    setTesting(sub.id);
    try {
      const r = await fetch(`${API}/api/admin/webhooks/subscriptions/${sub.id}/test`, { method: 'POST', headers: getAuth() });
      const d = await r.json();
      if (d.success) toast.success(`Test ping delivered (HTTP ${d.status_code})`);
      else toast.error(`Test failed: ${d.error || d.status_code}`);
    } catch { toast.error('Test failed'); }
    setTesting(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">{subs.length} subscription{subs.length !== 1 ? 's' : ''}</p>
        <button
          data-testid="add-subscription-btn"
          onClick={() => setModal('new')}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700"
        >
          <Plus size={15} /> Add Subscription
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : subs.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
          <Webhook size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500 font-medium">No subscriptions yet</p>
          <p className="text-sm text-gray-400 mt-1">Add a destination URL to start receiving webhook events</p>
        </div>
      ) : (
        <div className="space-y-3">
          {subs.map(sub => (
            <div key={sub.id} data-testid={`subscription-row-${sub.id}`} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-gray-900">{sub.name}</span>
                    <StatusBadge status={sub.status} />
                    {sub.auto_paused_at && (
                      <span className="text-xs text-red-500 flex items-center gap-1">
                        <AlertTriangle size={11} /> Auto-paused ({sub.failure_count} failures)
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 font-mono truncate mb-2">{sub.url}</p>
                  <div className="flex flex-wrap gap-1">
                    {sub.events?.map(ev => <EventTag key={ev} type={ev} />)}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    data-testid={`test-btn-${sub.id}`}
                    onClick={() => test(sub)}
                    disabled={testing === sub.id}
                    title="Send test ping"
                    className="p-2 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    {testing === sub.id ? <RefreshCw size={15} className="animate-spin" /> : <Send size={15} />}
                  </button>
                  <button
                    data-testid={`secret-btn-${sub.id}`}
                    onClick={() => setShowSecret(showSecret === sub.id ? null : sub.id)}
                    title="View signing secret"
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 transition-colors"
                  >
                    <Eye size={15} />
                  </button>
                  <button
                    onClick={() => setModal(sub)}
                    title="Edit"
                    className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-500 transition-colors"
                  >
                    <List size={15} />
                  </button>
                  <button
                    data-testid={`toggle-btn-${sub.id}`}
                    onClick={() => toggle(sub)}
                    title={sub.status === 'active' ? 'Pause' : 'Resume'}
                    className="p-2 rounded-lg border border-gray-200 hover:bg-amber-50 hover:border-amber-300 text-gray-500 hover:text-amber-600 transition-colors"
                  >
                    {sub.status === 'active' ? <Pause size={15} /> : <Play size={15} />}
                  </button>
                  <button
                    data-testid={`delete-btn-${sub.id}`}
                    onClick={() => remove(sub)}
                    title="Delete"
                    className="p-2 rounded-lg border border-gray-200 hover:bg-red-50 hover:border-red-300 text-gray-500 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              {showSecret === sub.id && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1 font-medium">HMAC Signing Secret — use this to verify incoming webhooks</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-gray-700 flex-1 truncate">{sub.secret}</code>
                    <button onClick={() => { navigator.clipboard.writeText(sub.secret); toast.success('Copied!'); }} className="p-1 hover:bg-gray-200 rounded">
                      <Copy size={13} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <SubscriptionModal
          sub={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSave={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
};

// ------------------------------------------------------------------ //
//  Events Tab                                                          //
// ------------------------------------------------------------------ //
const EventsTab = ({ refresh }) => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const LIMIT = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, skip });
      if (filter) params.set('event_type', filter);
      const r = await fetch(`${API}/api/admin/webhooks/events?${params}`, { headers: getAuth() });
      const d = await r.json();
      setEvents(d.events || []);
      setTotal(d.total || 0);
    } catch { toast.error('Failed to load events'); }
    setLoading(false);
  }, [filter, skip]);

  useEffect(() => { load(); }, [load, refresh]);
  useEffect(() => { setSkip(0); }, [filter]);

  const loadDetail = async (eventId) => {
    if (expanded === eventId) { setExpanded(null); setDetail(null); return; }
    setExpanded(eventId);
    try {
      const r = await fetch(`${API}/api/admin/webhooks/events/${eventId}`, { headers: getAuth() });
      setDetail(await r.json());
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <select
          data-testid="event-type-filter"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Event Types</option>
          {Object.entries(EVENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={() => load()} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <RefreshCw size={13} /> Refresh
        </button>
        <span className="text-sm text-gray-400 ml-auto">{total} total events</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : events.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
          <Activity size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500 font-medium">No events yet</p>
          <p className="text-sm text-gray-400 mt-1">Events appear here when students log in, are created, or complete lessons</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {events.map(ev => (
              <div key={ev.id} className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                <button
                  data-testid={`event-row-${ev.id}`}
                  onClick={() => loadDetail(ev.id)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <EventTag type={ev.event_type} />
                  <span className="text-sm text-gray-700 flex-1 truncate">
                    {ev.payload?.student?.email || ev.payload?.student?.first_name || '—'}
                  </span>
                  <span className="text-xs text-gray-400 flex-shrink-0">{fmt(ev.created_at)}</span>
                  <div className="flex gap-1 flex-shrink-0">
                    {ev.delivery_summary?.delivered > 0 && <span className="text-xs text-emerald-600 font-medium">{ev.delivery_summary.delivered} delivered</span>}
                    {ev.delivery_summary?.failed > 0 && <span className="text-xs text-red-500 font-medium">{ev.delivery_summary.failed} failed</span>}
                    {ev.delivery_summary?.pending > 0 && <span className="text-xs text-amber-500 font-medium">{ev.delivery_summary.pending} pending</span>}
                  </div>
                  {expanded === ev.id ? <ChevronDown size={15} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />}
                </button>
                {expanded === ev.id && detail && detail.id === ev.id && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Payload</p>
                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono text-gray-700 overflow-auto max-h-48">
                          {JSON.stringify(detail.payload, null, 2)}
                        </pre>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Deliveries ({detail.deliveries?.length || 0})</p>
                        <div className="space-y-2">
                          {(detail.deliveries || []).map(d => (
                            <div key={d.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs">
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-gray-700 truncate">{d.subscription_name}</span>
                                <StatusBadge status={d.status} />
                              </div>
                              <span className="text-gray-400">{d.total_attempts} attempt{d.total_attempts !== 1 ? 's' : ''} · last {fmt(d.last_attempt_at)}</span>
                            </div>
                          ))}
                          {(!detail.deliveries || detail.deliveries.length === 0) && <p className="text-gray-400 text-xs">No deliveries</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center mt-4 text-sm text-gray-500">
            <button onClick={() => setSkip(Math.max(0, skip - LIMIT))} disabled={skip === 0} className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50">← Prev</button>
            <span>{skip + 1}–{Math.min(skip + LIMIT, total)} of {total}</span>
            <button onClick={() => setSkip(skip + LIMIT)} disabled={skip + LIMIT >= total} className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50">Next →</button>
          </div>
        </>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ //
//  Deliveries Tab                                                      //
// ------------------------------------------------------------------ //
const DeliveriesTab = ({ refresh }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const [resending, setResending] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const LIMIT = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, skip });
      if (statusFilter) params.set('status', statusFilter);
      const r = await fetch(`${API}/api/admin/webhooks/deliveries?${params}`, { headers: getAuth() });
      const d = await r.json();
      setDeliveries(d.deliveries || []);
      setTotal(d.total || 0);
    } catch { toast.error('Failed to load deliveries'); }
    setLoading(false);
  }, [statusFilter, skip]);

  useEffect(() => { load(); }, [load, refresh]);
  useEffect(() => { setSkip(0); }, [statusFilter]);

  const resend = async (deliveryId) => {
    setResending(deliveryId);
    try {
      const r = await fetch(`${API}/api/admin/webhooks/deliveries/${deliveryId}/resend`, { method: 'POST', headers: getAuth() });
      const d = await r.json();
      if (d.success) { toast.success('Queued for resend'); load(); }
      else toast.error('Failed to resend');
    } catch { toast.error('Error'); }
    setResending(null);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <select
          data-testid="delivery-status-filter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="delivered">Delivered</option>
          <option value="failed">Failed</option>
          <option value="retrying">Retrying</option>
          <option value="pending">Pending</option>
        </select>
        <button onClick={() => load()} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <RefreshCw size={13} /> Refresh
        </button>
        <span className="text-sm text-gray-400 ml-auto">{total} total deliveries</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : deliveries.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
          <CheckCircle2 size={32} className="mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500 font-medium">No deliveries yet</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {deliveries.map(d => (
              <div key={d.id} className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 flex flex-wrap items-center gap-3">
                  <StatusBadge status={d.status} />
                  <EventTag type={d.event_type} />
                  <span className="text-sm text-gray-700 flex-1 min-w-0 truncate">{d.subscription_name}</span>
                  <span className="text-xs text-gray-400">{d.total_attempts} attempts</span>
                  <span className="text-xs text-gray-400">{fmt(d.last_attempt_at || d.created_at)}</span>
                  <button
                    data-testid={`expand-delivery-${d.id}`}
                    onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                    className="p-1.5 rounded border border-gray-200 hover:bg-gray-50 text-gray-400"
                  >
                    {expanded === d.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  </button>
                  {(d.status === 'failed' || d.status === 'delivered') && (
                    <button
                      data-testid={`resend-btn-${d.id}`}
                      onClick={() => resend(d.id)}
                      disabled={resending === d.id}
                      className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {resending === d.id ? <RefreshCw size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                      Resend
                    </button>
                  )}
                </div>
                {expanded === d.id && (
                  <div className="px-4 pb-4 border-t border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-3 mb-2">Attempt History</p>
                    <div className="space-y-2">
                      {(d.attempts || []).map((a, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-3 text-xs bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                          <span className="font-medium text-gray-600">#{a.attempt_number}</span>
                          {a.success
                            ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={11} /> Success</span>
                            : <span className="text-red-500 flex items-center gap-1"><XCircle size={11} /> Failed</span>}
                          {a.response_status && <span className="text-gray-500">HTTP {a.response_status}</span>}
                          <span className="text-gray-400">{a.duration_ms}ms</span>
                          <span className="text-gray-400">{fmt(a.attempted_at)}</span>
                          {a.error && <span className="text-red-400 truncate max-w-xs">{a.error}</span>}
                        </div>
                      ))}
                      {(!d.attempts || d.attempts.length === 0) && <p className="text-xs text-gray-400">No attempts recorded yet</p>}
                    </div>
                    {d.next_retry_at && <p className="text-xs text-amber-600 mt-2">Next retry: {fmt(d.next_retry_at)}</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center mt-4 text-sm text-gray-500">
            <button onClick={() => setSkip(Math.max(0, skip - LIMIT))} disabled={skip === 0} className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50">← Prev</button>
            <span>{skip + 1}–{Math.min(skip + LIMIT, total)} of {total}</span>
            <button onClick={() => setSkip(skip + LIMIT)} disabled={skip + LIMIT >= total} className="px-3 py-1 border rounded-lg disabled:opacity-40 hover:bg-gray-50">Next →</button>
          </div>
        </>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ //
//  Main Page                                                           //
// ------------------------------------------------------------------ //
export default function WebhookManager({ user, onLogout }) {
  const [tab, setTab] = useState('subscriptions');
  const [stats, setStats] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/webhooks/stats`, { headers: getAuth() });
      if (r.ok) setStats(await r.json());
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats, refreshKey]);

  const refresh = () => setRefreshKey(k => k + 1);

  const TABS = [
    { id: 'subscriptions', label: 'Subscriptions', icon: Webhook },
    { id: 'events', label: 'Event Log', icon: Activity },
    { id: 'deliveries', label: 'Delivery Log', icon: CheckCircle2 },
  ];

  return (
    <Layout user={user} onLogout={onLogout} currentPage="webhook-manager">
      <div className="p-4 sm:p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Webhook size={24} className="text-blue-600" />
              Webhook Manager
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage destinations, monitor events, and resend failed deliveries
            </p>
          </div>
          <button
            onClick={refresh}
            data-testid="global-refresh-btn"
            className="flex items-center gap-2 border border-gray-200 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw size={14} /> Refresh All
          </button>
        </div>

        <StatsBar stats={stats} />

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6 gap-1">
          {TABS.map(t => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors
                ${tab === t.id
                  ? 'border-b-2 border-blue-600 text-blue-600 -mb-px bg-white'
                  : 'text-gray-500 hover:text-gray-700'}`}
            >
              <t.icon size={15} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'subscriptions' && <SubscriptionsTab refresh={refreshKey} />}
        {tab === 'events'        && <EventsTab        refresh={refreshKey} />}
        {tab === 'deliveries'    && <DeliveriesTab    refresh={refreshKey} />}
      </div>
    </Layout>
  );
}
